import 'react-app-polyfill/ie11';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import Modal from 'react-modal';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { Origin, UserRole } from '@upstart/harmony/lib/constants';
import { Route, Switch } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { ThemeProvider } from '@mui/material/styles';
import { DeskingTheme } from '@upstart/desking-components';
import ModalContainer from 'react-modal-promise';
import WebsocketService from '@upstart/harmony/lib/websocket-service';
import { ToastContainer } from 'react-toastify';
import '@upstart/shared-components/style.css';
import 'react-toastify/dist/ReactToastify.css';

import 'semantic-ui-css/semantic.min.css';
import 'react-bootstrap-typeahead/css/Typeahead.css';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-bootstrap-table/dist/react-bootstrap-table-all.min.css';
import 'react-bootstrap-toggle/dist/bootstrap2-toggle.css';
import 'bootstrap-daterangepicker/daterangepicker.css';

import './index.scss';
import { GrowthBookProvider } from 'hooks/GrowthBook';
import store from './redux/store';
import sagas from './redux/sagas';
import App from './containers/App';
import PrivateRoute from './containers/PrivateRoute';
import ManagerDashboard from './containers/ManagerDashboard';
import DatadogRum from './containers/DatadogRum';
import Dealers from './containers/Dealers';
import Feeds from './containers/Feeds';
import Feed from './containers/Feed';
import FeedviewContainer from './containers/Feedview';
import Feedviews from './containers/Feedviews';
import FeedMappings from './containers/FeedMappings';
import FeedMapping from './containers/FeedMapping';
import Groups from './containers/Groups';
import Group from './containers/Group';
import Roles from './containers/Roles';
import Role from './containers/Role';
import Dealer from './containers/Dealer';
import InternalDashboard from './containers/InternalDashboard';
import Deal from './containers/Deal';
import Pricing from './containers/Pricing';
import ProtectionPlans from './containers/ProtectionPlans';
import Users from './containers/Users';
import User from './containers/User';
import Logins from './containers/Logins';
import Login from './containers/Login';
import SourceMappings from './containers/SourceMappings';
import SourceMapping from './containers/SourceMapping';
import Auth from './containers/Auth';
import OfferReview from './containers/PencilEditor/OfferReview';
import Forms from './containers/Forms';
import Form from './containers/Form';
import OnlineFormPacks from './containers/OnlineFormPacks';
import OnlineFormPack from './containers/OnlineFormPack';
import FormPacks from './containers/FormPacks';
import FormPack from './containers/FormPack';
import DealerFlow from './containers/DealerFlow';
import history from './router/history';
import CreditReport from './containers/CreditReport';
import Buttons from './containers/Buttons';
import Screenshot from './containers/Screenshot';
import { ResultsDealer, ResultsUser } from './containers/ResultsDashboard';
import IndexRedirect from './containers/IndexRedirect';
import PricingMatrix from './containers/PricingMatrix';
import DealerDataRevisions from './containers/DealerDataRevisions';
import UserImportEditor from './containers/UserImportEditor';
import DealerText from './containers/DealerText';
import DealerTextEditor from './containers/DealerTextEditor';
import DealerApplicantForm from './containers/DealerApplicantForm';
import Accessories from './containers/Accessories';
import Accessory from './containers/Accessory';
import Websites from './containers/Websites';
import WebsiteContainer from './containers/Website';
import Fonts from './containers/Fonts';
import UpstartLoanStatus from './containers/UpstartLoanStatus';
import OutreachFormEditor from './containers/OutreachFormEditor';
import OutreachForms from './containers/OutreachForms';
import InstoreForm from './containers/InstoreForm';
import CommandCenterForm from './containers/CommandCenterForm';
import useCommandCenterListener from './hooks/useCommandCenterListener';

import Appointments from './containers/Appointments';
import { byPassMaintenanceMode } from './redux/global/actions';
import WithSystemDownCheckRoute from './containers/WithSystemDownCheckRoute';
import LendingSupport from './containers/LendingSupport/LendingSupport';
import { RicSchemaEditor, RicSchemaIndex } from './containers/RicSchemaAdmin';
import {
  getSearchParameters,
  getSystemStatus,
} from './util/systemMaintenanceUtils';
import { isEmbeddedInCommandCenter } from './util/commandCenter';
import { init as initAmplitude } from './util/amplitude';

// Init sagas
sagas.map(store.runSaga);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

initAmplitude();

const params = getSearchParameters();
if ('disableMaintenanceMode' in params && params.disableMaintenanceMode) {
  store.dispatch(byPassMaintenanceMode(true));
}

if (isEmbeddedInCommandCenter()) {
  document.body.classList.add('in-command-center');
}

setInterval(getSystemStatus, 60 * 1000);

function Root() {
  useCommandCenterListener();

  useEffect(() => {
    Modal.setAppElement(document.getElementById('root'));
  }, []);

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ConnectedRouter history={history}>
          <ThemeProvider theme={DeskingTheme}>
            <GrowthBookProvider>
              <div id="app">
                <Route component={App} />
                <DatadogRum />
                <ModalContainer />
                <Switch>
                  <WithSystemDownCheckRoute
                    path="/login"
                    component={Auth}
                    componentProps={{
                      didResetPwd:
                        'didResetPwd' in params && params.didResetPwd,
                    }}
                  />
                  <WithSystemDownCheckRoute
                    path="/expiredSession"
                    component={Auth}
                  />
                  <PrivateRoute
                    path="/dashboard"
                    component={ManagerDashboard}
                    componentProps={{ view: 'instore' }}
                  />
                  <PrivateRoute
                    path="/dashboard-online"
                    component={ManagerDashboard}
                    componentProps={{
                      leadSource: Origin.ONLINE,
                      view: 'online',
                    }}
                  />

                  <PrivateRoute path="/dealers" component={Dealers} />
                  <PrivateRoute path="/groups" component={Groups} />
                  <PrivateRoute path="/websites" component={Websites} />
                  <PrivateRoute path="/fonts" component={Fonts} />
                  <PrivateRoute
                    path="/website/:websiteId/config"
                    component={WebsiteContainer}
                    componentProps={{ isConfigRoute: true }}
                  />
                  <PrivateRoute
                    path="/website/:websiteId/theme"
                    component={WebsiteContainer}
                    componentProps={{ isThemeRoute: true }}
                  />
                  <PrivateRoute
                    path="/website/:websiteId/flow"
                    component={WebsiteContainer}
                    componentProps={{ isFlowRoute: true }}
                  />
                  <PrivateRoute
                    path="/website/:websiteId/vdp"
                    component={WebsiteContainer}
                    componentProps={{ isButtonsRoute: true }}
                  />
                  <PrivateRoute
                    path="/website/:websiteId/srp"
                    component={WebsiteContainer}
                    componentProps={{ isButtonsRoute: true }}
                  />
                  <PrivateRoute
                    path="/website/:websiteId/standalone"
                    component={WebsiteContainer}
                    componentProps={{ isButtonsRoute: true }}
                  />
                  <PrivateRoute
                    path="/website/:websiteId/api"
                    component={WebsiteContainer}
                    componentProps={{ isButtonsRoute: true }}
                  />
                  <PrivateRoute
                    path="/website/:websiteId/standalone-products"
                    component={WebsiteContainer}
                    componentProps={{ isStandaloneProducts: true }}
                  />
                  <PrivateRoute
                    path="/website"
                    component={WebsiteContainer}
                    componentProps={{ isConfigRoute: true }}
                  />
                  <PrivateRoute
                    path="/employees/user/:userId/access"
                    component={User}
                    componentProps={{
                      isEmployeeRoute: true,
                      changeAccess: true,
                    }}
                  />
                  <PrivateRoute
                    path="/employees/user/:userId/edit"
                    component={User}
                    componentProps={{ isEmployeeRoute: true }}
                  />
                  <PrivateRoute
                    path="/employees/user/:userId/changePassword"
                    component={User}
                    componentProps={{
                      isEmployeeRoute: true,
                      changePassword: true,
                    }}
                  />
                  <PrivateRoute
                    path="/employees/user/"
                    component={User}
                    componentProps={{ isEmployeeRoute: true }}
                  />
                  <PrivateRoute
                    path="/employees"
                    component={Users}
                    componentProps={{ isEmployeeRoute: true }}
                  />
                  <PrivateRoute path="/feeds" component={Feeds} />
                  <PrivateRoute path="/feed" component={Feed} />
                  <PrivateRoute path="/feed/:feedId" component={Feed} />
                  <PrivateRoute path="/feedviews" component={Feedviews} />
                  <PrivateRoute
                    path="/feedview/:feedviewId/config"
                    component={FeedviewContainer}
                    componentProps={{ isConfigRoute: true }}
                  />
                  <PrivateRoute
                    path="/feedview/:feedviewId/filters"
                    component={FeedviewContainer}
                    componentProps={{ isFiltersRoute: true }}
                  />
                  <PrivateRoute
                    path="/feedview"
                    component={FeedviewContainer}
                    componentProps={{ isConfigRoute: true }}
                  />
                  <PrivateRoute path="/feedMappings" component={FeedMappings} />
                  <PrivateRoute path="/feedMapping" component={FeedMapping} />
                  <PrivateRoute
                    path="/feedMapping/:feedMappingId"
                    component={FeedMapping}
                  />
                  <PrivateRoute path="/roles" component={Roles} />
                  <PrivateRoute path="/role/:roleId" component={Role} />
                  <PrivateRoute
                    path="/dealer/:dealerId/:step?"
                    component={Dealer}
                  />
                  <PrivateRoute path="/dealer/" component={Dealer} />
                  <PrivateRoute
                    path="/:dealerId/pricing/:step?"
                    component={Pricing}
                  />
                  <PrivateRoute
                    path="/:dealerId/appointments"
                    component={Appointments}
                  />
                  <PrivateRoute
                    path="/:dealerId/protection-plans/:page?"
                    component={ProtectionPlans}
                  />
                  <PrivateRoute
                    path="/group/:groupId/:tab?/:step?"
                    component={Group}
                  />
                  <PrivateRoute path="/group" component={Group} />
                  <PrivateRoute
                    path="/internal-dashboard/:section?"
                    component={InternalDashboard}
                    roles={[
                      UserRole.ACCOUNT_MANAGER,
                      UserRole.DEALER_ADMIN,
                      UserRole.LIMITED_DEALER_ADMIN,
                    ]}
                  />
                  <PrivateRoute
                    path="/:dealerId/accessories/accessory/:id?"
                    component={Accessory}
                  />
                  <PrivateRoute
                    path="/:dealerId/accessories"
                    component={Accessories}
                  />
                  <PrivateRoute
                    path="/:dealerId/outreach/:templateId"
                    component={OutreachFormEditor}
                    roles={[
                      UserRole.SUPER_ADMIN,
                      UserRole.ADMIN,
                      UserRole.READ_ONLY_ADMIN,
                      UserRole.DEALER_ADMIN,
                      UserRole.LIMITED_DEALER_ADMIN,
                    ]}
                  />
                  <PrivateRoute
                    path="/:dealerId/outreach"
                    component={OutreachForms}
                    roles={[
                      UserRole.SUPER_ADMIN,
                      UserRole.ADMIN,
                      UserRole.READ_ONLY_ADMIN,
                      UserRole.DEALER_ADMIN,
                      UserRole.LIMITED_DEALER_ADMIN,
                    ]}
                  />
                  <PrivateRoute
                    path="/:dealerId/instore-config"
                    component={InstoreForm}
                    roles={[
                      UserRole.SUPER_ADMIN,
                      UserRole.ADMIN,
                      UserRole.READ_ONLY_ADMIN,
                      UserRole.DEALER_ADMIN,
                      UserRole.LIMITED_DEALER_ADMIN,
                    ]}
                  />
                  <PrivateRoute
                    path="/:dealerId/command-center"
                    component={CommandCenterForm}
                    roles={[
                      UserRole.SUPER_ADMIN,
                      UserRole.ADMIN,
                      UserRole.READ_ONLY_ADMIN,
                    ]}
                  />
                  <PrivateRoute path="/deal/:dealId" component={Deal} />
                  <PrivateRoute
                    path="/:dealerId/users/user/:userId/changePassword"
                    component={User}
                    componentProps={{ changePassword: true }}
                  />
                  <PrivateRoute
                    path="/:dealerId/users/import/:importType"
                    component={UserImportEditor}
                  />
                  <PrivateRoute
                    path="/:dealerId/users/user/:userId"
                    component={User}
                  />
                  <PrivateRoute
                    path="/:dealerId/users/user/"
                    component={User}
                  />
                  <PrivateRoute path="/:dealerId/users" component={Users} />
                  <PrivateRoute
                    path="/:dealerId/crmLogins/crmlogin/:loginId"
                    component={Login}
                  />
                  <PrivateRoute
                    path="/:dealerId/crmLogins/crmlogin/"
                    component={Login}
                  />
                  <PrivateRoute
                    path="/:dealerId/crmlogins"
                    component={Logins}
                  />
                  <PrivateRoute
                    path="/:dealerId/sources"
                    component={SourceMappings}
                  />
                  <PrivateRoute
                    path="/:dealerId/source"
                    component={SourceMapping}
                  />
                  <PrivateRoute
                    path="/:dealerId/source/"
                    component={SourceMapping}
                  />
                  <PrivateRoute
                    path="/:dealerId/forms/form/:formId"
                    component={Form}
                  />
                  <PrivateRoute
                    path="/:dealerId/forms/form/"
                    component={Form}
                  />
                  <PrivateRoute path="/:dealerId/forms" component={Forms} />
                  <PrivateRoute
                    path="/:dealerId/onlineFormPacks/onlineFormPack/:formPackId"
                    component={OnlineFormPack}
                  />
                  <PrivateRoute
                    path="/:dealerId/onlineFormPacks/onlineFormPack/"
                    component={OnlineFormPack}
                  />
                  <PrivateRoute
                    path="/:dealerId/onlineFormPacks"
                    component={OnlineFormPacks}
                  />
                  <PrivateRoute
                    path="/:dealerId/formPacks/formPack/:formPackId"
                    component={FormPack}
                  />
                  <PrivateRoute
                    path="/:dealerId/formPacks/formPack/"
                    component={FormPack}
                  />
                  <PrivateRoute
                    path="/:dealerId/formPacks"
                    component={FormPacks}
                  />
                  <PrivateRoute path="/:dealerId/flow" component={DealerFlow} />
                  <PrivateRoute
                    path="/offer-review/:dealId/:responseId"
                    component={OfferReview}
                  />
                  <PrivateRoute
                    path="/credit-report/:dealId"
                    component={CreditReport}
                    roles={[
                      UserRole.DEALER_MANAGER,
                      UserRole.DEALER_ADMIN,
                      UserRole.LIMITED_DEALER_ADMIN,
                    ]}
                  />
                  <PrivateRoute path="/:dealerId/buttons" component={Buttons} />
                  <PrivateRoute
                    path="/results-dashboard"
                    component={ResultsDealer}
                    roles={[
                      UserRole.DEALER_MANAGER,
                      UserRole.DEALER_ADMIN,
                      UserRole.LIMITED_DEALER_ADMIN,
                    ]}
                  />
                  <PrivateRoute
                    path="/results/:userId"
                    component={ResultsUser}
                    roles={[
                      UserRole.DEALER_MANAGER,
                      UserRole.DEALER_ADMIN,
                      UserRole.LIMITED_DEALER_ADMIN,
                    ]}
                  />
                  <PrivateRoute
                    path="/pricing-matrix/:clientType"
                    component={PricingMatrix}
                    componentProps={{ embedded: isEmbeddedInCommandCenter() }}
                    roles={[UserRole.PRICING_MANAGEMENT]}
                  />
                  <PrivateRoute
                    path="/:dealerId/revisions"
                    component={DealerDataRevisions}
                    roles={[
                      UserRole.SUPER_ADMIN,
                      UserRole.ADMIN,
                      UserRole.READ_ONLY_ADMIN,
                    ]}
                  />
                  <PrivateRoute
                    path="/:dealerId/text/:clientType/:pageId"
                    component={DealerTextEditor}
                    roles={[
                      UserRole.SUPER_ADMIN,
                      UserRole.ADMIN,
                      UserRole.READ_ONLY_ADMIN,
                    ]}
                  />
                  <PrivateRoute
                    path="/:dealerId/text"
                    component={DealerText}
                    roles={[
                      UserRole.SUPER_ADMIN,
                      UserRole.ADMIN,
                      UserRole.READ_ONLY_ADMIN,
                    ]}
                  />
                  <PrivateRoute
                    path="/:dealerId/applicant-form"
                    component={DealerApplicantForm}
                    roles={[
                      UserRole.SUPER_ADMIN,
                      UserRole.ADMIN,
                      UserRole.READ_ONLY_ADMIN,
                    ]}
                  />
                  {/* Using an empty roles attribute causes only admins to be allowed on this page */}
                  <PrivateRoute
                    path="/screenshot/:date/:name"
                    component={Screenshot}
                    roles={[]}
                  />
                  <PrivateRoute
                    path="/upstart-loan-status"
                    component={UpstartLoanStatus}
                  />
                  <PrivateRoute
                    path="/lending-support/:dealId?"
                    component={LendingSupport}
                    roles={[
                      UserRole.SUPER_ADMIN,
                      UserRole.ADMIN,
                      UserRole.READ_ONLY_ADMIN,
                    ]}
                  />
                  <PrivateRoute
                    path="/admin/ric-schema/:stateCode"
                    component={RicSchemaEditor}
                    roles={[
                      UserRole.SUPER_ADMIN,
                      UserRole.ADMIN,
                      UserRole.READ_ONLY_ADMIN,
                    ]}
                  />
                  <PrivateRoute
                    path="/admin/ric-schema"
                    component={RicSchemaIndex}
                    roles={[
                      UserRole.SUPER_ADMIN,
                      UserRole.ADMIN,
                      UserRole.READ_ONLY_ADMIN,
                    ]}
                  />
                  <IndexRedirect />
                </Switch>
              </div>
            </GrowthBookProvider>
          </ThemeProvider>
        </ConnectedRouter>
      </QueryClientProvider>
      <ToastContainer />
    </Provider>
  );
}

ReactDOM.render(<Root />, document.getElementById('root'));

WebsocketService.connect(process.env.REACT_APP_WEBSOCKETS_URL);
